<template>
    <div
        class="col-span-12 lg:col-span-6 flex flex-col gap-4 bg-white dark:bg-slate-800 rounded-lg shadow dark:shadow-slate-900/50 p-6 pb-8 relative md:min-h-[400px]"
    >
        <!-- Header -->
        <div
            class="flex items-center justify-between mb-2"
            v-if="walletCardCount > 0"
        >
            <div class="flex items-center gap-2">
                <Icon
                    icon="coin"
                    width="24"
                    height="24"
                    class="text-slate-800 dark:text-slate-100"
                />
                <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100">
                    Benefits expiring soon
                </h3>
            </div>

            <!-- View All Link -->
            <Link :href="`/card-benefits`" class="link-base link-dark">
                View all
                <icon icon="arrow" />
            </Link>
        </div>

        <!-- NO cards in wallet state-->
        <div
            v-if="!loading && walletCardCount === 0"
            class="w-full flex flex-col items-center pt-4 justify-center"
        >
            <div
                class="w-full flex flex-col text-center items-center justify-center pb-6"
            >
                <img
                    src="../../../../images/monthly-benefits-state-promo.png"
                    alt="Monthly benefits"
                />
                <p class="mb-4">
                    <span class="font-bold text-slate-800 dark:text-slate-100 text-3xl">
                        Use your benefits
                    </span>
                    <br />
                    <span class="font-bold text-orange text-3xl">
                        before they're gone
                    </span>
                </p>
                <p class="text-slate-500 dark:text-slate-400 text-lg px-6 mb-6">
                    Keep track of benefits that are about to expire and
                    <br />
                    use them before they're gone.
                </p>
                <Link class="btn btn-orange" href="/card-benefits">
                    View my benefits
                </Link>
            </div>
        </div>

        <!-- Empty State -->
        <div
            v-if="
                !loading &&
                expiringBenefits &&
                expiringBenefits.length === 0 &&
                walletCardCount > 0
            "
            class="text-center py-12"
        >
            <div
                class="mx-auto w-16 h-16 mb-6 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center"
            >
                <Icon
                    icon="coin"
                    width="32"
                    height="32"
                    class="w-full h-full text-slate-800 dark:text-slate-100"
                />
            </div>
            <h4 class="text-xl font-medium text-slate-800 dark:text-slate-100 mb-2">
                No card benefits yet
            </h4>
            <p class="text-lg text-slate-500 dark:text-slate-400">
                Add a card to your wallet to see which
                <br />
                benefits are about to expire.
            </p>
        </div>

        <!-- Benefits List -->
        <template v-if="expiringBenefits && expiringBenefits.length > 0">
            <div class="flex flex-col flex-1">
                <div
                    v-for="(benefit, index) in displayedBenefits"
                    :key="`${benefit.benefit_id}-${benefit.connected_account_id}`"
                    class="flex items-center justify-between gap-4 py-4 border-b border-slate-100 dark:border-slate-700 last:border-b-0"
                >
                    <!-- Left: Benefit Amount and Title -->
                    <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2 mb-1 md:hidden">
                            <p
                                class="text-sm font-medium px-2 py-0.5 rounded"
                                :class="
                                    benefit.days_until_expiration <= 7
                                        ? 'text-red-600 dark:text-red-200 bg-red-100 dark:bg-red-500/30'
                                        : 'text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700'
                                "
                            >
                                {{
                                    benefit.days_until_expiration === 0
                                        ? 'Today'
                                        : benefit.days_until_expiration === 1
                                          ? '1 day'
                                          : `${benefit.days_until_expiration} days`
                                }}
                            </p>
                            <p
                                class="text-sm text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded"
                            >
                                {{ Filters.formatMoney(benefit.amount_left) }}
                                left
                            </p>
                        </div>

                        <p class="text-base font-medium text-slate-800 dark:text-slate-100">
                            {{ benefit.benefit_title }}
                        </p>
                        <div class="flex items-center gap-2 mt-1">
                            <img
                                :src="benefit.card_image_url"
                                :alt="benefit.card_title"
                                class="w-10 h-6 rounded-sm object-cover"
                            />
                            <p class="text-sm text-slate-500 dark:text-slate-400">
                                {{ benefit.card_title }} ({{
                                    benefit.card_last_four
                                }})
                            </p>
                        </div>
                    </div>

                    <!-- Right: Days and Amount Left -->
                    <div class="flex items-center gap-4 shrink-0">
                        <div class="hidden text-right md:block">
                            <p
                                class="text-sm text-center inline-block font-medium px-2 py-0.5 mb-1 rounded"
                                :class="
                                    benefit.days_until_expiration <= 7
                                        ? 'text-red-600 dark:text-red-200 bg-red-100 dark:bg-red-500/30'
                                        : 'text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700'
                                "
                            >
                                {{
                                    benefit.days_until_expiration === 0
                                        ? 'Today'
                                        : benefit.days_until_expiration === 1
                                          ? '1 day'
                                          : `${benefit.days_until_expiration} days`
                                }}
                            </p>
                            <p class="text-sm text-slate-500 dark:text-slate-400">
                                {{ Filters.formatMoney(benefit.amount_left) }}
                                left
                            </p>
                        </div>

                        <!-- Add Usage Button -->
                        <button
                            @click="openAddUsageDropdown(benefit, $event)"
                            class="flex items-center justify-center w-10 h-10 rounded-md border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shrink-0"
                            :title="`Add usage for ${benefit.benefit_title}`"
                        >
                            <Icon
                                icon="plus"
                                width="30"
                                height="30"
                                class="text-slate-700 dark:text-slate-300"
                            />
                        </button>
                    </div>
                </div>
            </div>

            <!-- Show More/Less Button -->
            <button
                v-if="expiringBenefits.length > 3"
                @click="toggleShowAll"
                class="mt-4 text-sm font-medium text-slate-700 hover:text-slate-800 dark:text-slate-100 dark:hover:text-white text-left rounded w-fit"
            >
                {{ showAll ? 'Show less' : 'Show all' }}
            </button>
        </template>

        <!-- Add Usage Dropdown -->
        <teleport to="body">
            <div
                v-if="showAddUsageDropdown"
                class="fixed inset-0 z-50"
                @click.self="closeAddUsageDropdown"
            >
                <div
                    ref="dropdownElement"
                    class="absolute bg-white dark:bg-slate-800 rounded-l bg-white rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.06),0px_1px_3px_0px_rgba(0,0,0,0.10)] overflow-hidden w-96 p-4"
                    :style="dropdownStyle"
                >
                    <!-- Form -->
                    <div class="flex items-center gap-4">
                        <!-- Amount Input -->
                        <div>
                            <UiInput
                                v-model:inputValue="usageForm.amount"
                                id="usage-amount"
                                name="usage-amount"
                                label="Amount used"
                                placeholder="0.00"
                                :options="{
                                    icon: 'dollar',
                                    hideLabel: true,
                                }"
                            />
                        </div>

                        <div class="relative">
                            <UiInput
                                id="bonus-date"
                                name="bonus_date"
                                label=""
                                v-model="usageForm.date"
                                :options="{
                                    icon: 'calendar',
                                    hideLabel: true,
                                }"
                            >
                                <template v-slot:input>
                                    <FlatPickr
                                        :config="dateConfig"
                                        name="date_received"
                                        :modelValue="usageForm.date"
                                        id="date-received"
                                        class="relative block w-full rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1.5 pl-10 text-slate-800 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 placeholder:text-sm"
                                        @on-change="
                                            (dates, v) => dateChange(dates)
                                        "
                                    />
                                </template>
                            </UiInput>
                        </div>
                    </div>

                    <p
                        v-if="usageForm.amount > selectedBenefit?.amount_left"
                        class="mt-1 text-xs text-red-600"
                    >
                        Amount cannot exceed
                        {{ Filters.formatMoney(selectedBenefit?.amount_left) }}
                    </p>

                    <!-- Actions -->
                    <div class="flex items-center justify-between gap-2 pt-2">
                        <p class="text-sm text-slate-500 dark:text-slate-400 mt-2">
                            {{
                                Filters.formatMoney(
                                    selectedBenefit?.amount_left
                                )
                            }}
                            left
                        </p>
                        <button
                            @click="handleUsageSubmit"
                            :disabled="!isFormValid || submitting"
                            class="btn btn-primary !w-40 flex items-center gap-2"
                        >
                            <Icon
                                v-if="!submitting"
                                icon="check-lg"
                                width="16"
                                height="16"
                            />
                            {{ submitting ? 'Saving...' : 'Redeem' }}
                        </button>
                    </div>
                </div>
            </div>
        </teleport>
        <Sheet
            :open="sheetOpen"
            title="Add usage"
            size="xs"
            @update:open="sheetOpen = $event"
        >
            <div class="mb-3">
                <p class="text-slate-500 dark:text-slate-400 text-sm mb-2">
                    {{ selectedBenefit?.benefit_title }} -
                    {{ selectedBenefit?.card_title }}
                </p>
            </div>
            <div class="w-full flex flex-col gap-4 mb-6">
                <UiInput
                    v-model:inputValue="usageForm.amount"
                    id="usage-amount"
                    name="usage-amount"
                    label="Redeemed"
                    placeholder="0.00"
                    :options="{
                        icon: 'dollar',
                    }"
                />
                <p
                    v-if="usageForm.amount > selectedBenefit?.amount_left"
                    class="mt-1 text-xs text-red-600"
                >
                    Amount cannot exceed
                    {{ Filters.formatMoney(selectedBenefit?.amount_left) }}
                </p>
                <UiInput
                    id="usage-date"
                    name="usage_date"
                    label="Date"
                    v-model="usageForm.date"
                    :options="{
                        icon: 'calendar',
                    }"
                >
                    <template v-slot:input>
                        <FlatPickr
                            :config="dateConfig"
                            name="useage_date"
                            :modelValue="usageForm.date"
                            id="usage-date"
                            class="relative block w-full rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1.5 pl-10 text-slate-800 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 placeholder:text-sm"
                            @on-change="(dates, v) => dateChange(dates)"
                        />
                    </template>
                </UiInput>
            </div>

            <div class="flex justify-between gap-2">
                <button
                    @click="sheetOpen = false"
                    class="btn btn-secondary !w-40"
                >
                    Cancel
                </button>

                <button
                    @click="handleUsageSubmit"
                    :disabled="!isFormValid || submitting"
                    class="btn btn-primary flex items-center gap-2 !w-auto"
                >
                    <Icon
                        v-if="!submitting"
                        icon="check-lg"
                        width="16"
                        height="16"
                    />
                    {{ submitting ? 'Saving...' : 'Redeem' }}
                </button>
            </div>
        </Sheet>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { Link } from '@inertiajs/vue3';
import { storeToRefs } from 'pinia';
import { notify } from 'notiwind';
import dayjs from 'dayjs';
import { useDashboardStore } from '@/store/dashboard';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import FlatPickr from '@/components/ui/SafeFlatPickr.vue';
import Icon from '@/components/ui/icon/icon.vue';
import Sheet from '@/components/ui/sheet/sheet.vue';
import Filters from '@/filters';

const dashboardStore = useDashboardStore();
const { expiringBenefits, walletCardCount, loading } =
    storeToRefs(dashboardStore);

const sheetOpen = ref(false);
const showAddUsageDropdown = ref(false);
const selectedBenefit = ref(null);
const showAll = ref(false);
const dropdownElement = ref(null);
const dropdownStyle = ref({});
const submitting = ref(false);
const usageDate = ref(dayjs().format('MM/DD/YYYY'));

const usageForm = ref({
    amount: '',
    date: dayjs().format('MM/DD/YYYY'),
});

let viewWidth = window.innerWidth;
let dateConfig = {
    dateFormat: 'm/d/Y',
    maxDate: 'today',
    minDate: null,
    disableMobile: 'true',
};

const isFormValid = computed(() => {
    return (
        usageForm.value.amount &&
        parseFloat(usageForm.value.amount) > 0 &&
        parseFloat(usageForm.value.amount) <=
            (selectedBenefit.value?.amount_left || 0) &&
        usageDate.value
    );
});

// Show 3 benefits initially, or all if showAll is true
const displayedBenefits = computed(() => {
    if (!expiringBenefits.value) return [];
    return showAll.value
        ? expiringBenefits.value
        : expiringBenefits.value.slice(0, 3);
});

const toggleShowAll = () => {
    showAll.value = !showAll.value;
};

const openAddUsageDropdown = (benefit, event) => {
    selectedBenefit.value = benefit;

    usageDate.value = dayjs().format('MM/DD/YYYY');

    // Reset form
    usageForm.value = {
        amount: '',
        date: dayjs().format('MM/DD/YYYY'),
    };

    if (viewWidth < 769) {
        sheetOpen.value = true;
    } else {
        showAddUsageDropdown.value = true;
        // Position dropdown near the clicked button
        setTimeout(() => {
            if (dropdownElement.value && event.target) {
                const buttonRect = event.target
                    .closest('button')
                    .getBoundingClientRect();
                const dropdownWidth = 384; // w-96 = 24rem = 384px
                const dropdownHeight = 400; // approximate height

                let top = buttonRect.bottom + 8;
                let left = buttonRect.right - dropdownWidth;

                // Adjust if dropdown would go off screen
                if (left < 8) {
                    left = 8;
                }
                if (top + dropdownHeight > window.innerHeight) {
                    top = buttonRect.top - dropdownHeight - 8;
                }

                dropdownStyle.value = {
                    top: `${top}px`,
                    left: `${left}px`,
                };
            }
        }, 10);
    }
};

const closeAddUsageDropdown = () => {
    showAddUsageDropdown.value = false;
    selectedBenefit.value = null;
};

const dateChange = (v) => {
    usageDate.value = v[0];
};

const handleUsageSubmit = async () => {
    if (!isFormValid.value || submitting.value) return;

    try {
        submitting.value = true;
        const benefit = selectedBenefit.value;
        const walletId = dashboardStore.walletId;

        // Prepare payload for the API
        const payload = {
            card_benefit_id: benefit.card_benefit_id,
            connected_account_id: benefit.connected_account_id,
            total_amount_used: parseFloat(usageForm.value.amount),
            used_at: usageDate.value,
            notes: '',
        };

        // Call the API to save the usage
        await window.ApiService.apiCall(
            `/wallets/${walletId}/card-benefits`,
            'POST',
            payload
        );

        // Show success notification
        notify(
            {
                group: 'success',
                title: 'Usage Recorded',
                text: 'Benefit usage has been saved successfully',
                type: 'success',
            },
            4000
        );

        closeAddUsageDropdown();

        // Refresh the expiring benefits data
        await dashboardStore.getExpiringBenefits();
    } catch (error) {
        console.error('Failed to save usage:', error);

        // Show error notification
        notify(
            {
                group: 'error',
                title: 'Save Failed',
                text:
                    error?.response?.data?.message ||
                    'Could not save usage record. Please try again.',
                type: 'error',
            },
            4000
        );
    } finally {
        submitting.value = false;
    }
};

const updateViewWidth = () => {
    viewWidth = window.innerWidth;
};

onMounted(() => {
    updateViewWidth();
    window.addEventListener('resize', updateViewWidth);
});
onUnmounted(() => {
    viewWidth = null;
    window.removeEventListener('resize', updateViewWidth);
});
</script>
